/**
 * Supervivencia de subidas largas en móvil.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * El problema
 * ─────────────────────────────────────────────────────────────────────────────
 * Los navegadores móviles suspenden las pestañas en segundo plano de forma
 * agresiva, y iOS Safari es el peor caso: al bloquear la pantalla o cambiar de
 * app, las peticiones en vuelo se cortan sin aviso y los timers se congelan.
 *
 * Con una subida que en Perú puede durar dos horas, esto no es un caso raro:
 * es lo que va a pasar SIEMPRE si el usuario sube desde el móvil.
 *
 * No hay forma de impedirlo. Lo que sí se puede hacer es:
 *   1. Pedir Wake Lock para que la pantalla no se apague sola mientras sube.
 *   2. Avisar al usuario de que no cambie de app, con lenguaje claro.
 *   3. Detectar la suspensión y reanudar automáticamente al volver.
 *   4. Persistir el progreso en cada parte confirmada, para no perder trabajo.
 *
 * El punto 4 es el que de verdad salva la experiencia, y por eso las partes son
 * de 8 MiB: al volver, como mucho se repite una parte.
 */

export function isMobile(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

/**
 * Mantiene la pantalla encendida durante la subida.
 *
 * Sólo funciona con la pestaña visible y no está en todos los navegadores
 * (notablemente ausente en Safari iOS durante años). Es una mejora, no una
 * garantía: el diseño no puede depender de ello.
 */
export class ScreenWakeGuard {
  private sentinel: WakeLockSentinel | null = null

  async acquire(): Promise<boolean> {
    if (!('wakeLock' in navigator)) return false

    try {
      this.sentinel = await navigator.wakeLock.request('screen')

      // El sistema puede revocarlo al ocultar la pestaña; se re-pide al volver.
      document.addEventListener('visibilitychange', this.onVisibilityChange)
      return true
    } catch {
      return false
    }
  }

  /** addEventListener descarta el valor de retorno: envolvemos con void
   *  para que un rechazo no quede sin capturar. */
  private readonly onVisibilityChange = (): void => {
    void this.reacquire()
  }

  private readonly reacquire = async (): Promise<void> => {
    if (document.visibilityState === 'visible' && this.sentinel?.released !== false) {
      try {
        this.sentinel = await navigator.wakeLock.request('screen')
      } catch {
        /* sin wake lock; la subida sigue, sólo es menos cómoda */
      }
    }
  }

  async release(): Promise<void> {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    await this.sentinel?.release()
    this.sentinel = null
  }
}

/**
 * Avisa cuando la pestaña vuelve a primer plano tras haber estado oculta.
 *
 * El gestor de subidas lo usa para reanudar: al volver, comprueba qué partes
 * quedaron confirmadas y continúa desde ahí en lugar de empezar de cero.
 */
export function onResumeFromBackground(callback: (hiddenForMs: number) => void): () => void {
  let hiddenAt: number | null = null

  const handler = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      return
    }

    if (hiddenAt !== null) {
      callback(Date.now() - hiddenAt)
      hiddenAt = null
    }
  }

  document.addEventListener('visibilitychange', handler)
  return () => document.removeEventListener('visibilitychange', handler)
}

/**
 * Aviso del navegador al cerrar con subidas activas.
 *
 * En móvil es poco fiable —muchos navegadores lo ignoran— pero en desktop
 * evita la pérdida accidental más común: cerrar la pestaña a media subida.
 */
export function warnBeforeUnload(hasActiveUploads: () => boolean): () => void {
  const handler = (event: BeforeUnloadEvent) => {
    if (!hasActiveUploads()) return
    event.preventDefault()
    event.returnValue = ''
  }

  window.addEventListener('beforeunload', handler)
  return () => window.removeEventListener('beforeunload', handler)
}
